import SEOHead, { DOMAIN } from './SEOHead';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Shield, Lock, Mail, Eye, Cloud, Phone, CheckCircle, ArrowRight, Clock, Users, AlertTriangle } from 'lucide-react';
import { useRef, useState } from 'react';

const FEATURES = [
  { icon: Shield, title: 'Advanced Endpoint Protection', desc: 'Next-gen antivirus and anti-malware across all your devices.' },
  { icon: Mail, title: 'Email Security & Anti-Phishing', desc: 'Block malicious emails before they reach your team.' },
  { icon: Lock, title: 'Multi-Factor Authentication', desc: 'Add a critical second layer of security to all accounts.' },
  { icon: Cloud, title: 'Automatic Cloud Backups', desc: 'Your data backed up and recoverable — always.' },
  { icon: Eye, title: '24/7 Threat Monitoring', desc: 'Real-time detection and response to suspicious activity.' },
  { icon: AlertTriangle, title: 'Security Awareness Training', desc: 'Turn your team into your first line of defense.' },
];

const STATS = [
  { value: '43%', label: 'of cyberattacks target small businesses' },
  { value: '197', label: 'average days to detect a breach' },
  { value: '$4.45M', label: 'average cost of a data breach' },
  { value: '60%', label: 'of SMBs close within 6 months of an attack' },
];

// VideoObject Schema for AI assistants and rich video snippets
const videoSchema = {
  "@context": "https://schema.org",
  "@type": "VideoObject",
  "name": "Secure365 - Enterprise Cybersecurity for Toronto Businesses",
  "description": "Learn how Secure365 by King of IT provides enterprise-grade cybersecurity protection for Toronto small and medium businesses. 24/7 monitoring, email security, endpoint protection, and cloud backups starting at $15/user/month.",
  "thumbnailUrl": `${DOMAIN}/videos/secure365-thumbnail.jpg`,
  "uploadDate": "2024-06-01",
  "duration": "PT2M30S",
  "contentUrl": `${DOMAIN}/videos/Secure365_KingOfIT_Final.mp4`,
  "embedUrl": `${DOMAIN}/secure365`,
  "publisher": {
    "@type": "Organization",
    "name": "King of IT",
    "logo": {
      "@type": "ImageObject",
      "url": `${DOMAIN}/logo.png`
    }
  },
  "potentialAction": {
    "@type": "WatchAction",
    "target": `${DOMAIN}/secure365`
  }
};

// Product Schema for Secure365 cybersecurity service
const productSchema = {
  "@context": "https://schema.org",
  "@type": "Product",
  "name": "Secure365 Cybersecurity Package",
  "description": "Enterprise-grade cybersecurity protection for Toronto businesses. Includes 24/7 threat monitoring, email security, endpoint protection, MFA setup, cloud backups, and security awareness training.",
  "brand": {
    "@type": "Brand",
    "name": "King of IT"
  },
  "offers": {
    "@type": "Offer",
    "priceCurrency": "CAD",
    "price": "15.00",
    "priceValidUntil": "2025-12-31",
    "availability": "https://schema.org/InStock",
    "url": `${DOMAIN}/secure365`,
    "seller": {
      "@type": "Organization",
      "name": "King of IT"
    }
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "5.0",
    "reviewCount": "47",
    "bestRating": "5"
  }
};

export default function Secure365Page() {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  return (
    <div data-testid="secure365-page">
      <SEOHead
        title="Secure365 - Complete Cybersecurity for Toronto Businesses | King of IT"
        description="Secure365 by King of IT: Enterprise-grade cybersecurity for Toronto SMBs. 24/7 monitoring, email security, endpoint protection, cloud backups. From $15/user/month. Free assessment."
        path="/secure365"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(videoSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
      </Helmet>

      {/* Hero with Video */}
      <section className="bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text */}
            <div>
              <span className="inline-block bg-yellow-600/20 text-yellow-500 text-sm font-semibold px-4 py-1.5 rounded-full mb-6 border border-yellow-600/30">
                SECURE365 BY KING OF IT
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Cybersecurity That <span className="text-yellow-500">Never Sleeps</span>
              </h1>
              <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                Enterprise-grade protection designed for Toronto businesses with 10-200 employees. 
                Stop worrying about threats and start focusing on growth.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a
                  href="tel:6479513653"
                  className="inline-flex items-center justify-center gap-2 bg-yellow-600 hover:bg-yellow-500 text-white font-semibold px-8 py-4 rounded-lg transition-colors text-lg"
                  data-testid="secure365-cta-call"
                >
                  <Phone className="w-5 h-5" />
                  Free Assessment
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 border-2 border-white/20 hover:border-white/40 text-white font-semibold px-8 py-4 rounded-lg transition-colors"
                >
                  Learn More
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
              <p className="text-sm text-slate-400">Starting at <span className="text-white font-semibold">$15/user/month</span> — No contracts required</p>
            </div>

            {/* Right - Video Player */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-black/50 border border-slate-700/50">
                <video
                  ref={videoRef}
                  controls
                  preload="metadata"
                  poster=""
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  className="w-full aspect-video bg-slate-800"
                  data-testid="secure365-video"
                >
                  <source src="/videos/Secure365_KingOfIT_Final.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                {/* Play overlay */}
                {!isPlaying && (
                  <button
                    onClick={handlePlay}
                    className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/20 transition-colors group cursor-pointer"
                    data-testid="video-play-overlay"
                  >
                    <div className="w-20 h-20 bg-yellow-600 group-hover:bg-yellow-500 rounded-full flex items-center justify-center shadow-xl transition-all group-hover:scale-110">
                      <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </button>
                )}
              </div>
              <p className="text-center text-sm text-slate-500 mt-3">Watch: How Secure365 protects your business</p>
            </div>
          </div>
        </div>
      </section>

      {/* Threat Stats */}
      <section className="bg-slate-900 border-y border-slate-800 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {STATS.map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-3xl font-bold text-yellow-500">{stat.value}</p>
                <p className="text-sm text-slate-400 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">Everything You Need to Stay Protected</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">Secure365 is a layered cybersecurity solution — not just antivirus. Every angle is covered.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feat, i) => (
              <div key={i} className="p-6 rounded-xl border border-slate-200 hover:border-yellow-500/50 hover:shadow-lg transition-all group">
                <feat.icon className="w-10 h-10 text-yellow-600 mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{feat.title}</h3>
                <p className="text-slate-600 text-sm">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-10 text-center">How Secure365 Works</h2>
          <div className="space-y-8">
            {[
              { step: '01', title: 'Free Security Assessment', desc: 'We audit your current IT environment, identify vulnerabilities, and show you exactly where you\'re exposed.' },
              { step: '02', title: 'Custom Protection Plan', desc: 'Based on your assessment, we build a Secure365 package tailored to your business size, industry, and compliance needs.' },
              { step: '03', title: 'Seamless Deployment', desc: 'Our team deploys all security layers without disrupting your operations. Most setups complete within 48 hours.' },
              { step: '04', title: '24/7 Monitoring & Response', desc: 'From day one, your business is actively monitored. Threats are detected and neutralized before they cause damage.' },
            ].map((item, i) => (
              <div key={i} className="flex gap-6 items-start">
                <div className="w-12 h-12 bg-yellow-600 text-white rounded-xl flex items-center justify-center font-bold text-lg flex-shrink-0">
                  {item.step}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">{item.title}</h3>
                  <p className="text-slate-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">Simple, Transparent Pricing</h2>
          <p className="text-slate-600 mb-10">No hidden fees. No long-term contracts. Cancel anytime.</p>
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <div className="p-8 rounded-2xl border-2 border-slate-200 hover:border-yellow-500 transition-colors">
              <h3 className="text-lg font-semibold text-slate-500 mb-2">Secure365 Essential</h3>
              <p className="text-4xl font-bold text-slate-900 mb-1">$15<span className="text-lg text-slate-500">/user/mo</span></p>
              <p className="text-sm text-slate-500 mb-6">Core protection for most businesses</p>
              <ul className="text-left space-y-3 text-sm">
                {['Endpoint protection', 'Email security', 'MFA setup', 'Monthly reports', 'Business-hours support'].map((f, i) => (
                  <li key={i} className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" /> {f}</li>
                ))}
              </ul>
            </div>
            <div className="p-8 rounded-2xl border-2 border-yellow-500 bg-yellow-50/30 relative">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-yellow-600 text-white text-xs font-bold px-4 py-1 rounded-full">RECOMMENDED</span>
              <h3 className="text-lg font-semibold text-yellow-700 mb-2">Secure365 Complete</h3>
              <p className="text-4xl font-bold text-slate-900 mb-1">$35<span className="text-lg text-slate-500">/user/mo</span></p>
              <p className="text-sm text-slate-500 mb-6">Full protection + 24/7 monitoring</p>
              <ul className="text-left space-y-3 text-sm">
                {['Everything in Essential', '24/7 threat monitoring', 'Security awareness training', 'Dark web monitoring', 'Cloud backup & recovery', 'Incident response', 'Compliance assistance'].map((f, i) => (
                  <li key={i} className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" /> {f}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-12 bg-yellow-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">Get Your Free Security Assessment</h2>
          <p className="text-yellow-100 mb-6 text-lg">Find out where your business is vulnerable — no cost, no obligation.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:6479513653" className="inline-flex items-center justify-center gap-2 bg-white text-yellow-700 font-bold px-8 py-4 rounded-lg hover:bg-yellow-50 transition-colors text-lg">
              <Phone className="w-5 h-5" />
              Call 647-951-3653
            </a>
            <a href="mailto:support@kingofit.ca" className="inline-flex items-center justify-center gap-2 border-2 border-white text-white font-semibold px-8 py-4 rounded-lg hover:bg-yellow-700 transition-colors">
              <Mail className="w-5 h-5" />
              support@kingofit.ca
            </a>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="py-10 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 flex flex-wrap items-center justify-center gap-8 text-sm text-slate-500">
          <div className="flex items-center gap-2"><Shield className="w-5 h-5 text-yellow-600" /> Microsoft-Certified Technicians</div>
          <div className="flex items-center gap-2"><Clock className="w-5 h-5 text-yellow-600" /> Rapid Response Support</div>
          <div className="flex items-center gap-2"><Users className="w-5 h-5 text-yellow-600" /> 500+ Businesses Served</div>
          <div className="flex items-center gap-2"><Lock className="w-5 h-5 text-yellow-600" /> PIPEDA Compliance Support</div>
        </div>
      </section>
    </div>
  );
}
